import { Link } from "react-router-dom";
import { displayCompanyName } from "../utils/companyName";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { UnassignedCompaniesWidget } from "../components/UnassignedCompaniesWidget";
import { LatestNewsWidget } from "../components/LatestNewsWidget";
import { PendingOrdersWidget } from "../components/PendingOrdersWidget";
import { ContractsActionWidget } from "../components/ContractsActionWidget";
import { lazy, Suspense } from "react";
// Lazy: CooperationSnapshot pulls in recharts and the two role dashboards are
// large; splitting them keeps the shell + dashboard skeleton chunk small.
const CooperationSnapshot = lazy(() =>
  import("../components/CooperationSnapshot").then((m) => ({ default: m.CooperationSnapshot })),
);
const SalesDashboard = lazy(() => import("./SalesDashboard"));
const QualityDashboard = lazy(() => import("./QualityDashboard"));
import type { TFunction } from "i18next";

type QuickLink = { to: string; label: string; description: string; permission?: string };

// Links a customer (CUST_*) sees on the dashboard. Day-to-day work first.
// Contract is intentionally NOT a shortcut — partners sign one rarely (5+ years),
// so the sidebar entry is enough; the dashboard surfaces ongoing collaboration.
const customerLinks = (t: TFunction): QuickLink[] => [
  { to: "/orders/new", label: t("dashboard.link.createOrder.label", "Create order"), description: t("dashboard.link.createOrder.desc", "Start a new order for review by your sales rep."), permission: "orders:create" },
  { to: "/orders", label: t("dashboard.link.myOrders.label", "My orders"), description: t("dashboard.link.myOrders.desc", "Track submitted, in-review and approved orders."), permission: "orders:view" },
  { to: "/forecasts", label: t("dashboard.link.forecasts.label", "Forecasts"), description: t("dashboard.link.forecasts.desc", "Submit demand forecast for upcoming periods."), permission: "forecasts:view" },
  { to: "/catalog", label: t("dashboard.link.catalog.label", "Browse catalog"), description: t("dashboard.link.catalog.desc", "Find pumps by category, attribute or search.") },
  { to: "/pump-selector", label: t("dashboard.link.pumpSelector.label", "Pump selector"), description: t("dashboard.link.pumpSelector.desc", "Pick the right pump for the project.") },
  { to: "/materials", label: t("dashboard.link.materials.label", "Materials"), description: t("dashboard.link.materials.desc", "Brochures, datasheets, certificates.") },
  { to: "/faq", label: t("dashboard.link.faq.label", "FAQ"), description: t("dashboard.link.faq.desc", "Frequently asked questions.") },
  { to: "/tutorials", label: t("dashboard.link.tutorials.label", "Service tutorials"), description: t("dashboard.link.tutorials.desc", "Installation, commissioning, troubleshooting."), permission: "service:view" },
];

// Links an internal user (ADMIN/SALES/MARKETING/SERVICE/SUPERADMIN) sees.
const internalLinks = (t: TFunction): QuickLink[] => [
  { to: "/catalog", label: t("dashboard.link.catalog.label", "Browse catalog"), description: t("dashboard.link.catalog.desc", "Find pumps by category, attribute or search.") },
  { to: "/pump-selector", label: t("dashboard.link.pumpSelector.label", "Pump selector"), description: t("dashboard.link.pumpSelector.desc", "Pick the right pump for the project.") },
  { to: "/materials", label: t("dashboard.link.materials.label", "Materials"), description: t("dashboard.link.materials.desc", "Brochures, datasheets, certificates.") },
  { to: "/news", label: t("dashboard.link.news.label", "News"), description: t("dashboard.link.news.desc", "Latest IMP announcements and updates.") },
  { to: "/admin/companies", label: t("dashboard.link.manageCompanies.label", "Manage companies"), description: t("dashboard.link.manageCompanies.desc", "Customer accounts and sales assignments."), permission: "companies:create" },
  { to: "/admin/users", label: t("dashboard.link.manageUsers.label", "Manage users"), description: t("dashboard.link.manageUsers.desc", "Invite and manage portal users."), permission: "users:invite_customer_any_company" },
  { to: "/admin/catalog/products", label: t("dashboard.link.manageProducts.label", "Manage products"), description: t("dashboard.link.manageProducts.desc", "Catalog content and assets."), permission: "catalog:edit" },
  { to: "/admin/materials", label: t("dashboard.link.uploadMaterials.label", "Upload materials"), description: t("dashboard.link.uploadMaterials.desc", "Add brochures or other DAM assets."), permission: "materials:upload" },
  { to: "/admin/news", label: t("dashboard.link.manageNews.label", "Manage news"), description: t("dashboard.link.manageNews.desc", "Draft, publish or archive articles."), permission: "news:create" },
];

export default function DashboardPage() {
  const { state, hasPermission } = useAuth();
  const { t } = useTranslation();
  if (state.status !== "authenticated") return null;
  const { user } = state;

  // SALES (without admin) gets a focused dashboard centered on companies in
  // their scope, ticket queue, TM tasks/projects, and upcoming meetings.
  const isAdmin = user.roles.includes("ADMIN") || user.roles.includes("SUPERADMIN");
  const isSales =
    (user.roles.includes("SALES") || user.roles.includes("SALES_DIRECTOR")) && !isAdmin;
  if (isSales) return <Suspense fallback={null}><SalesDashboard /></Suspense>;

  // Quality control (SERVICE) gets its own tighter dashboard — KPI + ticket
  // queue + my tasks — instead of the generic links grid that follows.
  const isQuality = user.roles.includes("SERVICE") && !isAdmin;
  if (isQuality) return <Suspense fallback={null}><QualityDashboard /></Suspense>;

  const isCustomer = user.roles.some((r) => r.startsWith("CUST_"));
  const linkSet = isCustomer ? customerLinks(t) : internalLinks(t);
  const visibleLinks = linkSet.filter((l) => {
    // Customer with Forecasts disabled doesn't see the Forecasts shortcut.
    if (l.to === "/forecasts" && isCustomer && !user.forecastsEnabled) return false;
    return !l.permission || hasPermission(l.permission);
  });

  const hour = new Date().getHours();
  const timeGreetingKey =
    hour < 12 ? "dashboard.morning" : hour < 18 ? "dashboard.afternoon" : "dashboard.evening";
  const timeGreetingFallback =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("dashboard.breadcrumb.aria", "Breadcrumb")}>
            <span>{t("nav.home", { defaultValue: "Home" })}</span>
            <span>{t("nav.dashboard", { defaultValue: "Dashboard" })}</span>
          </nav>
          <h1>
            {t(timeGreetingKey, { defaultValue: timeGreetingFallback })}, {user.firstName}
          </h1>
          {user.companyName && <div className="sub">{displayCompanyName(user.companyName)}</div>}
        </div>
      </div>

      {hasPermission("orders:view") && <Suspense fallback={null}><CooperationSnapshot /></Suspense>}

      <div className="dashboard-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 style={sectionTitle}>{t("dashboard.quickLinks", "Quick links")}</h2>
          <div style={linkGrid}>
            {visibleLinks.map((l) => (
              <Link key={l.to} to={l.to} style={linkCard}>
                <strong style={{ color: "#0b3a6f", fontSize: 14 }}>{l.label}</strong>
                <span style={{ color: "#6b7280", fontSize: 12 }}>{l.description}</span>
              </Link>
            ))}
          </div>
        </div>
        <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {hasPermission("orders:view") && <PendingOrdersWidget limit={5} />}
          {/* Contracts widget only for internal users — customers sign one
              every several years, so it's noise on a daily-use dashboard. */}
          {!isCustomer && hasPermission("contracts:view") && <ContractsActionWidget limit={5} />}
          {/* Only for whoever can actually assign a rep — same authority the
              assignment endpoints require. The widget hides itself at zero. */}
          {hasPermission("companies:delete") && <UnassignedCompaniesWidget />}
          <LatestNewsWidget limit={3} />
        </aside>
      </div>
    </div>
  );
}

const sectionTitle: React.CSSProperties = { margin: 0, fontSize: 13, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 };
const linkGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 };
const linkCard: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4,
  padding: 14, background: "#fff", borderRadius: 10,
  boxShadow: "0 1px 2px rgba(0,0,0,.04)", textDecoration: "none",
};
